import apiService from './api';

class AnalyticsService {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.visitorData = null;
    this.isTracking = false;
  }

  // Get existing session ID or create a new one
  getOrCreateSessionId() {
    const STORAGE_KEY = 'radio_session_id';
    const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const sessionData = JSON.parse(stored);
        const now = Date.now();
        
        // Check if session is still valid (within 24 hours)
        if (now - sessionData.createdAt < SESSION_DURATION) {
          // Update last activity time
          sessionData.lastActivity = now;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
          return sessionData.sessionId;
        } else {
          // Session expired, create new one
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      // If localStorage fails, continue with new session
      console.debug('Failed to read session from localStorage:', error);
    }
    
    // Create new session
    const newSessionId = this.generateSessionId();
    const sessionData = {
      sessionId: newSessionId,
      createdAt: Date.now(),
      lastActivity: Date.now()
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.debug('Failed to save session to localStorage:', error);
    }
    
    return newSessionId;
  }

  // Generate a unique session ID
  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Track new visitor
  async trackVisitor() {
    if (this.isTracking) return; // Prevent duplicate tracking
    
    try {
      this.isTracking = true;
      
      // Store visitor data
      this.visitorData = {
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language
      };

      // Track visitor via Django API
      await apiService.trackVisitor();
      
    } catch (error) {
      // Silent fail for analytics errors
      this.isTracking = false;
    }
  }

  // Get visitor statistics
  async getVisitorStats() {
    try {
      const stats = await apiService.getAnalyticsStats();
      return stats.visitor_stats || { total_visitors: 0 };
    } catch (error) {
      // Error getting visitor stats
      return { total_visitors: 0 };
    }
  }

  // Get location statistics
  async getLocationStats() {
    try {
      const stats = await apiService.getLocationStats();
      return stats || { countries: {}, cities: {}, regions: {} };
    } catch (error) {
      // Error getting location stats
      return { countries: {}, cities: {}, regions: {} };
    }
  }

  // Get daily statistics
  async getDailyStats(date = new Date().toISOString().split('T')[0]) {
    try {
      const stats = await apiService.getDailyStats(date);
      return stats || { count: 0 };
    } catch (error) {
      // Error getting daily stats
      return { count: 0 };
    }
  }

  // ===== ENHANCED TRACKING METHODS =====

  // Track audio engagement events
  async trackAudioEngagement(action, stationData, additionalData = {}) {
    try {
      const engagementData = {
        session_id: this.sessionId,
        station_id: stationData.id,
        station_name: stationData.name,
        country_name: stationData.country_name || stationData.country || 'Unknown',
        action: action,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        ...additionalData
      };

      await apiService.trackAudioEngagement(engagementData);
    } catch (error) {
      // Silent fail for analytics errors
      console.debug('Audio engagement tracking failed:', error);
    }
  }

  // Track user journey events
  async trackUserJourney(eventType, eventData = {}, pageContext = null) {
    try {
      const journeyData = {
        session_id: this.sessionId,
        event_type: eventType,
        event_data: eventData,
        page_context: pageContext,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language
      };

      await apiService.trackUserJourney(journeyData);
    } catch (error) {
      // Silent fail for analytics errors
      console.debug('User journey tracking failed:', error);
    }
  }

  // Track search analytics
  async trackSearch(searchType, searchTerm, resultsCount, clickedResult = null) {
    try {
      const searchData = {
        session_id: this.sessionId,
        search_type: searchType,
        search_term: searchTerm,
        results_count: resultsCount,
        clicked_result: clickedResult,
        user_agent: navigator.userAgent,
        language: navigator.language
      };

      console.log('🔍 Tracking search analytics:', searchData);
      await apiService.trackSearchAnalytics(searchData);
      console.log('✅ Search analytics tracked successfully');
    } catch (error) {
      // Silent fail for analytics errors
      console.error('❌ Search analytics tracking failed:', error);
    }
  }

  // Track enhanced visitor session
  async trackVisitorSession(additionalData = {}) {
    try {
      const sessionData = {
        session_id: this.sessionId,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        ...additionalData
      };

      await apiService.trackVisitorSession(sessionData);
    } catch (error) {
      // Silent fail for analytics errors
      console.debug('Visitor session tracking failed:', error);
    }
  }

  // Get engagement statistics
  async getEngagementStats() {
    try {
      const stats = await apiService.getEngagementStats();
      return stats.engagement_stats || {};
    } catch (error) {
      console.debug('Error getting engagement stats:', error);
      return {};
    }
  }

  // Get visitor numbers
  async getVisitorNumbers() {
    try {
      const stats = await apiService.getVisitorNumbers();
      return stats.visitor_numbers || {};
    } catch (error) {
      console.debug('Error getting visitor numbers:', error);
      return {};
    }
  }

  // Get session ID
  getSessionId() {
    return this.sessionId;
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
